import React, { useMemo } from "react";

import {
  cleanedSeasonCategoricalScores,
  getCategoryMetrics,
} from "@/features/compare-players/utils/handleCategoryMetric";
import ChartComparison from "@/features/compare-players/components/organisms/ChartComparison";

import { CategoryMetricChartProps } from "@/components/organisms/CategoryMetricChart/CategoryMetricChartView";
import {
  PlayerDetailPlayerMetaFragment,
  WatchlistPlayerFragment,
  WatchlistPlayerSeasonCategoricalScoreFragment,
} from "@/generated/graphql";
import { useSafeFont } from "@/hooks/useSafeFont";

/**
 *
 */
interface Props {
  currentPlayer: PlayerDetailPlayerMetaFragment | null | undefined;
  selectedPlayer: WatchlistPlayerFragment | null;
}

/**
 *
 */
interface CategoryMetric extends Omit<
  CategoryMetricChartProps,
  "playerPositions" | "name" | "loading" | "font"
> {}

/**
 * Container component for category metrics comparison between two players
 * Processes and displays categorical scores (carrying, dribbling, passing, etc.) for both players
 * @param root0 - Component props
 * @param root0.currentPlayer - The primary player being viewed (current player detail)
 * @param root0.selectedPlayer - The player being compared against the current player
 */
const CategoryMetricsComparisonContainer = ({
  currentPlayer,
  selectedPlayer,
}: Props) => {
  const font = useSafeFont(12);

  const currentPlayerName = useMemo(
    () => currentPlayer?.name ?? "",
    [currentPlayer]
  );
  const selectedPlayerName = useMemo(
    () => selectedPlayer?.name ?? "",
    [selectedPlayer]
  );

  const currentPlayerSeasonCategoricalScores = useMemo(
    () =>
      cleanedSeasonCategoricalScores(
        (currentPlayer?.seasonCategoricalScores ??
          []) as WatchlistPlayerSeasonCategoricalScoreFragment[]
      ),
    [currentPlayer?.seasonCategoricalScores]
  );

  const selectedPlayerSeasonCategoricalScores = useMemo(
    () =>
      cleanedSeasonCategoricalScores(
        (selectedPlayer?.seasonCategoricalScores ??
          []) as WatchlistPlayerSeasonCategoricalScoreFragment[]
      ),
    [selectedPlayer?.seasonCategoricalScores]
  );

  const carryingData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "carryingTransPositionalCategoricalScore"
    ),
    title: "Carrying",
  };

  const dribblingData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "dribblingTransPositionalCategoricalScore"
    ),
    title: "Dribbling",
  };

  const gkPassingData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "gkPassingTransCategoricalScore"
    ),
    title: "Passing",
  };

  const gkPositioningData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "gkPositioningTransCategoricalScore"
    ),
    title: "Positioning",
  };

  const gkShotStoppingData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "gkShotStoppingTransCategoricalScore"
    ),
    title: "Shot Stopping",
  };

  const passingData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "passingTransPositionalCategoricalScore"
    ),
    title: "Passing",
  };

  const shotLocationData: CategoryMetric = {
    ...getCategoryMetrics(
      currentPlayerSeasonCategoricalScores,
      currentPlayer?.id ?? "",
      selectedPlayerSeasonCategoricalScores,
      selectedPlayer?.id ?? "",
      "shotLocationTransPositionalCategoricalScore"
    ),
    title: "Shooting",
  };

  return (
    <>
      {[
        carryingData,
        dribblingData,
        gkPassingData,
        gkPositioningData,
        gkShotStoppingData,
        passingData,
        shotLocationData,
      ].map((categoryMetric, index) =>
        !categoryMetric?.data?.length ? null : (
          <ChartComparison
            key={`${index}`}
            {...categoryMetric}
            font={font}
            currentPlayerName={currentPlayerName}
            selectedPlayerName={selectedPlayerName}
          />
        )
      )}
    </>
  );
};

export default CategoryMetricsComparisonContainer;
